import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
} from "react-aria-components";
import { assignGroupToShift, useShiftsForSite } from "@/api/Shifts";
import NoResults from "@/components/NoResults/NoResults";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import type {
  IScheduledShift,
  IMedicalCompetence,
} from "@medoma/models/shifts";
import {
  calculateAvailableShifts,
  medicalCompetenceDictionary,
  shiftName,
} from "@medoma/models/shifts";
import type { SiteId } from "@medoma/models/sites";
import { deducedError, displayErrorMessageAlert } from "@/Utils/ErrorUtils";
import { ShiftTime } from "@/components/Time/ShiftTime";
import { Text } from "@medoma/components/Text/Text";
import * as Sentry from "@sentry/react";
import { allocateShiftsToActivityOccurrence } from "@/api/Activities";
import {
  activityRequirementSchema,
  type IActivityRequirement,
} from "@medoma/models/activityRequirements";
import type { AssignShiftTarget } from "./AssignShiftAction";
import styles from "./AssignShiftPicker.module.scss";

type PickerProps = {
  target: AssignShiftTarget;
  assignedShiftIds: number[];
  shiftsFromDate: Date;
  unfulfilledRequirements: IActivityRequirement[];
  patientSiteId: SiteId;
  onSuccess: () => void;
};

const ShiftRow = ({
  employee,
  competence,
  startDateTime,
  endDateTime,
  relativeToDate,
}: Pick<
  IScheduledShift,
  "employee" | "competence" | "startDateTime" | "endDateTime"
> & { relativeToDate: Date }) => (
  <span className={styles.shift}>
    <Text element="span" size="base">
      {shiftName(
        { employee, competence },
        { competenceFormat: "full", employeeNameFormat: "full" },
      )}
    </Text>
    <ShiftTime
      startDateTime={startDateTime}
      endDateTime={endDateTime}
      relativeToDate={relativeToDate}
      size="small"
      color="faded"
      layout="horizontal"
    />
  </span>
);

export const AssignShiftPicker = ({
  target,
  assignedShiftIds,
  shiftsFromDate,
  unfulfilledRequirements,
  patientSiteId,
  onSuccess,
}: PickerProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const {
    data: scheduledShifts,
    isPending,
    isError,
    error,
  } = useShiftsForSite(shiftsFromDate, patientSiteId);

  const { mutate: assignShift, isPending: isAllocating } = useMutation({
    mutationFn: (shiftId: number) =>
      target.kind === "occurrence"
        ? allocateShiftsToActivityOccurrence(
            [shiftId],
            target.activityId,
            target.occurrenceId,
          )
        : assignGroupToShift(target.groupId, shiftId),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
    onError: (error) => {
      const message =
        target.kind === "occurrence"
          ? t`Det gick inte att lägga till medarbetaren på aktiviteten.`
          : t`Det gick inte att lägga till besöket på arbetspasset.`;
      displayErrorMessageAlert(`${message} ${deducedError(error)}`);
    },
  });

  if (isPending) {
    return <Loading message={t`Hämtar arbetspass`} />;
  }
  if (isError) {
    return <ErrorView error={error} />;
  }
  if (!scheduledShifts) {
    return null;
  }

  const availableShifts = calculateAvailableShifts({
    scheduledShifts,
    assignedShiftIds,
  });
  if (availableShifts.length === 0) {
    return <NoResults message={t`Ingen tillgänglig personal`} />;
  }

  const unfulfilledMedicalRequirements = unfulfilledRequirements.filter(
    (requirement): requirement is IMedicalCompetence =>
      requirement !== activityRequirementSchema.enum.DoubleStaffing,
  );

  const requirementGroups = unfulfilledMedicalRequirements.map(
    (requirement) => ({
      key: requirement,
      label: `${t(medicalCompetenceDictionary[requirement].long)} (${availableShifts.filter(({ competence }) => competence === requirement).length})`,
      shifts: availableShifts.filter(
        ({ competence }) => competence === requirement,
      ),
    }),
  );

  const otherShifts = availableShifts.filter(
    ({ competence }) => !unfulfilledMedicalRequirements.includes(competence),
  );

  const groups =
    otherShifts.length > 0
      ? [
          ...requirementGroups,
          { key: "other", label: t`Övriga`, shifts: otherShifts },
        ]
      : requirementGroups;

  return (
    <ListBox
      className={styles.list}
      aria-label={t`Tillgängliga arbetspass`}
      selectionMode="none"
      onAction={(key) => assignShift(Number(key))}
      disabledKeys={
        isAllocating ? availableShifts.map(({ id }) => id) : undefined
      }
    >
      {groups.map((group) => (
        <ListBoxSection
          key={group.key}
          id={group.key}
          className={styles.section}
        >
          <Header className={styles.label}>{group.label}</Header>
          {group.shifts.map(
            ({ id, employee, startDateTime, endDateTime, competence }) => (
              <ListBoxItem
                key={id}
                id={id}
                className={styles.item}
                textValue={shiftName(
                  { employee, competence },
                  { competenceFormat: "full", employeeNameFormat: "full" },
                )}
              >
                <ShiftRow
                  competence={competence}
                  employee={employee}
                  startDateTime={startDateTime}
                  endDateTime={endDateTime}
                  relativeToDate={shiftsFromDate}
                />
              </ListBoxItem>
            ),
          )}
        </ListBoxSection>
      ))}
    </ListBox>
  );
};

const ErrorView = ({ error }: { error: Error }) => {
  const { t } = useLingui();
  Sentry.captureException(error);
  return (
    <ErrorMessage
      message={`${t`Kunde inte hämta arbetspassen.`} ${deducedError(error)}`}
    />
  );
};
